import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SiteConfig } from '../../../config/site-config';
import { StorageService } from '../../core/services/storage/storage.service';
import { NotificationType } from '../../shared/models/notification-type';
import { DialogService } from '../../shared/services/dialog/dialog.service';
import { ErrorHandlerService } from '../../shared/services/error-handler/error-handler.service';
import { NotificationService } from '../../shared/services/notification/notification.service';
import { User } from '../../shared/services/user/models/user.model';
import { UserService } from '../../shared/services/user/user.service';
import { ManageLocationsService } from '../services/manage-locations.service';
import { ManageUsersService } from '../services/manage-users.service';
import { AddEditUserComponent } from './add-edit-user/add-edit-user.component';

@Component({
    selector: 'upsc-manage-users',
    templateUrl: './manage-users.component.html',
    styleUrls: ['./manage-users.component.scss'],
})
export class ManageUsersComponent implements OnInit {

    public users: any[];
    public locations: any[];
    public userIdToRemove: string;
    public customMessage: string;
    public currentUserId: string;
    public currentUserDefaultlocation: string;
    public currentUserIsAdmin: boolean;
    public editMode: boolean;
    public initManageUser = false;

    public dtOptions: any = {};
    public dtTrigger: Subject<any> = new Subject<any>();

    // @ViewChild('userRemoved') userRemoved: ElementRef;
    // @ViewChild('userAdded') userAdded: ElementRef;
    // @ViewChild('userUpdated') userUpdated: ElementRef;

    constructor(private manageUsersService: ManageUsersService,
                private manageLocationService: ManageLocationsService,
                private notificationService: NotificationService,
                private dialog: MatDialog,
                private dialogService: DialogService,
                private errorHandlerService: ErrorHandlerService,
                private translateService: TranslateService,
                private storageService: StorageService,
                private userService: UserService,
                public userToEdit: User,
    ) {
        this.userService.getCurrentUser().subscribe(
            (currentUser) => {
                this.currentUserId = currentUser?.userId;
                this.currentUserDefaultlocation = currentUser?.defaultLocation;
                this.currentUserIsAdmin = currentUser?.isAdmin;
                this.getUsers();
            },
        );
    }

    getUsers() {
        forkJoin([
            this.manageUsersService.getUsers(),
            this.manageLocationService.getLocations(),
        ])
            .pipe(
                catchError((error) => {
                    this.dtTrigger.next(null);
                    this.notificationService.notify(
                        this.errorHandlerService.getHttpErrorMessage(error),
                        this.translateService.instant('errorLoadingUsersLocations'),
                        NotificationType.ERROR);

                    return of(null);
                }),
            )
            .subscribe(
                (response) => {
                    if (response) {
                        this.users = response[0];
                        this.locations = response[1];
                    } else {
                        this.users = [];
                    }

                    this.dtTrigger.next(null);
                },
            );
    }

    getDefaultLocation(defaultLocationId) {
        const location = this.locations.filter(x => x.ContactId.toLowerCase() === defaultLocationId.toLowerCase());
        if (location && location.length > 0) {
            return '[' + location[0].NickName + '] - ' + location[0].StreetAddress;
        }

        return '';
    }

    confirmRemove(userId) {
        this.dialogService.confirm(
            this.translateService.instant('userRemoveConfirmation'),
            this.translateService.instant('WantToRemoveThisUser'))
            .subscribe(
                (isConfirmed) => {
                    if (!isConfirmed) {
                        return;
                    }

                    this.disableUser(userId);
                });
    }

    disableUser(userId) {
        this.manageUsersService.disableUser(userId)
            .subscribe(
                (response) => {
                    this.notificationService.notify(
                        this.translateService.instant('userRemovedWithNoAccess'),
                        this.translateService.instant('userRemoved'),
                        NotificationType.SUCCESS);
                    this.getUsers();
                },
                (error) => {
                    this.notificationService.notify(
                        this.errorHandlerService.getHttpErrorMessage(error),
                        this.translateService.instant('errorRemovingUser'),
                        NotificationType.ERROR);
                });
    }

    editUser(userId) {
        const dialogConfig: MatDialogConfig = {
            disableClose: true,
            width: '600px',
            data: {
                user: this.users.filter(x => x.UserId === userId)[0],
                editMode: true,
                locations: this.locations,
                currentUserIsAdmin: this.currentUserIsAdmin,
            },
        };

        const dialogRef = this.dialog.open(AddEditUserComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(
            (result) => {
                if (result && result.result === 'success') {
                    this.notificationService.notify(
                        this.translateService.instant('UserProfileChangesSuccessfullySaved'),
                        this.translateService.instant('userUpdatedSuccessfully'),
                        NotificationType.SUCCESS);
                    this.getUsers();
                } else if (result && result.result === 'removed') {
                    this.notificationService.notify(
                        this.translateService.instant('userRemovedWithNoAccess'),
                        this.translateService.instant('userRemoved'),
                        NotificationType.SUCCESS);
                    this.getUsers();
                }
            });
    }

    public addUser() {
        const dialogConfig: MatDialogConfig = {
            disableClose: true,
            width: '600px',
            data: {
                user: new User(),
                editMode: false,
                locations: this.locations,
                defaultLocation: this.currentUserDefaultlocation,
                currentUserIsAdmin: this.currentUserIsAdmin,
            },
        };

        const dialogRef = this.dialog.open(AddEditUserComponent, dialogConfig);

        dialogRef.afterClosed().subscribe((result) => {
            if (result && result.result === 'success') {
                this.notificationService.notify(
                    this.translateService.instant('userAddedWithEmailConfirmation'),
                    this.translateService.instant('userAddedSuccessfully'),
                    NotificationType.SUCCESS);
                this.getUsers();
            }
        });
    }

    toggleManageUserModal(editmode) {
        this.initManageUser = false; // To load the child modal again
        this.editMode = editmode;
        this.initManageUser = true;
    }

    public ngOnInit() {
        this.dtOptions = SiteConfig.tableConfig;
    }
}
